// Codes for what dietitians record and calculate in nutrition assessments
// (the production RD templates and their Estimated Nutritional Needs tool)
// where LOINC has no term. LOINC links were checked on the production
// terminology server; where LOINC has a term, the rules use it directly
// (ideal body weight 50064-5, usual body weight 75292-3, resting metabolic
// rate 69429-9).
import type { Coding } from '@medplum/fhirtypes'
import {
  type TemplateQuestion,
  templateQuestionConcept,
  templateQuestionLoincMap,
} from '#src/terminology/define.ts'
import { ASSESSMENT_QUESTIONS_SYSTEM } from '#src/terminology/types.ts'

const NUTRITION_QUESTIONS = [
  {
    code: 'goal-body-weight',
    display: 'Goal body weight',
    definition:
      'The body weight the dietitian sets as the goal, which estimated needs are calculated from',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: '[lb_av]' },
    unmatched: '90734-5 Body weight change goal is a direction, not a weight',
  },
  {
    code: 'energy-goal-per-kg-low',
    display: 'Energy goal per kg, low',
    definition: 'The low end of the energy goal, in kcal per kg per day',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'kcal/kg/d' },
    unmatched: 'LOINC has no estimated energy needs term',
  },
  {
    code: 'energy-goal-per-kg-high',
    display: 'Energy goal per kg, high',
    definition: 'The high end of the energy goal, in kcal per kg per day',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'kcal/kg/d' },
    unmatched: 'LOINC has no estimated energy needs term',
  },
  {
    code: 'protein-goal-per-kg-low',
    display: 'Protein goal per kg, low',
    definition: 'The low end of the protein goal, in g per kg per day',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'g/kg/d' },
    unmatched:
      '9080-3 and the timed protein intake terms are measured intake, not a goal',
  },
  {
    code: 'protein-goal-per-kg-high',
    display: 'Protein goal per kg, high',
    definition: 'The high end of the protein goal, in g per kg per day',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'g/kg/d' },
    unmatched:
      '9080-3 and the timed protein intake terms are measured intake, not a goal',
  },
  {
    code: 'percent-ideal-body-weight',
    display: 'Percent of ideal body weight',
    definition: 'Body weight as a percent of ideal body weight',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: '%' },
    loinc: {
      code: '111541-9',
      equivalence: 'relatedto',
      comment:
        'LOINC records the deviation from ideal weight as a ratio; this is the weight itself as a percent of ideal',
    },
  },
  {
    code: 'adjusted-body-weight',
    display: 'Adjusted body weight',
    definition:
      'Ideal body weight plus a quarter of the weight above it, used for needs when weight is well above ideal',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: '[lb_av]' },
    unmatched: 'LOINC has no adjusted body weight term',
  },
  {
    code: 'weight-change-from-usual',
    display: 'Weight change from usual',
    definition:
      'Current body weight less usual body weight; negative is a loss',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: '[lb_av]' },
    unmatched: 'LOINC weight change terms are questionnaire items, not amounts',
  },
  {
    code: 'percent-weight-change-from-usual',
    display: 'Percent weight change from usual',
    definition:
      'Weight change from usual body weight as a percent of usual body weight; negative is a loss',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: '%' },
    unmatched: 'LOINC has no percent weight change term',
  },
  {
    code: 'estimated-energy-needs-low',
    display: 'Estimated energy needs, low',
    definition: 'The low end of estimated daily energy needs',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'kcal/d' },
    unmatched: 'LOINC has no estimated energy needs term',
  },
  {
    code: 'estimated-energy-needs-high',
    display: 'Estimated energy needs, high',
    definition: 'The high end of estimated daily energy needs',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'kcal/d' },
    unmatched: 'LOINC has no estimated energy needs term',
  },
  {
    code: 'estimated-protein-needs-low',
    display: 'Estimated protein needs, low',
    definition: 'The low end of estimated daily protein needs',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'g/d' },
    unmatched: 'LOINC has no estimated protein needs term',
  },
  {
    code: 'estimated-protein-needs-high',
    display: 'Estimated protein needs, high',
    definition: 'The high end of estimated daily protein needs',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'g/d' },
    unmatched: 'LOINC has no estimated protein needs term',
  },
  {
    code: 'estimated-fluid-needs-low',
    display: 'Estimated fluid needs, low',
    definition: 'The low end of estimated daily fluid needs',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'mL/d' },
    unmatched: 'LOINC fluid intake terms are measured intake, not needs',
  },
  {
    code: 'estimated-fluid-needs-high',
    display: 'Estimated fluid needs, high',
    definition: 'The high end of estimated daily fluid needs',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: 'mL/d' },
    unmatched: 'LOINC fluid intake terms are measured intake, not needs',
  },
] as const satisfies readonly TemplateQuestion[]

type NutritionCode = (typeof NUTRITION_QUESTIONS)[number]['code']

// The coding of one of these codes, displayed as defined.
export function nutritionCoding(code: NutritionCode): Coding {
  const question = NUTRITION_QUESTIONS.find(
    (candidate) => candidate.code === code
  )
  return {
    system: ASSESSMENT_QUESTIONS_SYSTEM,
    code,
    display: question?.display ?? code,
  }
}

export const NUTRITION_QUESTION_CONCEPTS = NUTRITION_QUESTIONS.map(
  templateQuestionConcept
)

export const NUTRITION_QUESTION_LOINC_MAP = NUTRITION_QUESTIONS.map(
  templateQuestionLoincMap
)
